import React from "react";
import NoData, { hasData } from "./NoData";
import FitNumber from "./FitNumber";
import { NEUTRAL } from "../../utils/chartTheme";
import { formatCount } from "../../utils/numberFormat";

const DonutChart = ({ items = [], centerValue, centerLabel = "Total", size = 128, stroke = 14 }) => {
  const rows = items.map((item) => ({ ...item, value: Number(item.value) || 0 }));
  if (!hasData(rows.map((r) => r.value))) return <NoData />;

  const total = rows.reduce((sum, r) => sum + r.value, 0);
  const radius = (size - stroke) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const share = (v) => (total > 0 ? ((v / total) * 100).toFixed(1) : "0.0");

  let offset = 0;
  const arcs = rows
    .filter((r) => r.value > 0)
    .map((r) => {
      const length = (r.value / total) * circumference;
      const arc = (
        <circle
          key={r.name}
          cx={center}
          cy={center}
          r={radius}
          stroke={r.color}
          strokeWidth={stroke}
          fill="transparent"
          strokeDasharray={`${length} ${circumference}`}
          strokeDashoffset={-offset}
          className="transition-all duration-500"
        >
          <title>{`${r.name}: ${r.value.toLocaleString()} (${share(r.value)}%)`}</title>
        </circle>
      );
      offset += length;
      return arc;
    });

  const middle = centerValue ?? total;

  const legendRows = rows;
  const twoColumns = legendRows.length > 3;

  return (
    <div className="flex items-center justify-center gap-5 py-2">
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={NEUTRAL.track}
            strokeWidth={stroke}
            fill="transparent"
          />
          {arcs}
        </svg>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center text-center mx-auto"
          style={{ padding: stroke + 4 }}
        >
          <div className="text-lg font-bold text-gray-800 w-full leading-tight">
            {typeof middle === "number" ? <FitNumber value={middle} /> : formatCount(middle)}
          </div>
          <div className="text-xs text-gray-600">{centerLabel}</div>
        </div>
      </div>
      <ul
        className={`min-w-0 text-left ${
          twoColumns ? "grid grid-cols-2 gap-x-4 gap-y-2" : "space-y-2"
        }`}
      >
        {legendRows.map((r) => (
          <li key={r.name} className="flex items-start gap-2 text-xs">
            <span
              className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-0.5"
              style={{ backgroundColor: r.color }}
            />
            <div className="min-w-0">
              <div className="font-semibold text-gray-700 leading-tight">{r.name}</div>
              <div className="text-gray-500 leading-tight">
                <span className="font-bold text-gray-800">{r.value.toLocaleString()}</span>{" "}
                ({share(r.value)}%)
              </div>
              {r.detail && <div className="text-gray-400 leading-tight">{r.detail}</div>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DonutChart;
